import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Link2, Plus, RefreshCw, ShieldOff, Tags } from 'lucide-react';
import {
  KNOWN_DEVICE_NUMS,
  deviceAssignmentSummary,
  deviceDisplayName,
  formatCoords,
  type Device,
} from '@mytag/shared';
import {
  useArchiveDevice,
  useCapabilities,
  useDevices,
  useRefreshAll,
  useRefreshLocation,
  useRestoreDevice,
} from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader } from '@/components/layout/AppShell';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Select,
  LoadingState,
  Table,
  Td,
  Th,
} from '@/components/ui/primitives';
import { ErrorState } from '@/components/common/ErrorState';
import { BatteryGauge, PositionFreshnessBadge, StatusBadge } from '@/components/common/indicators';
import {
  DeviceActionsMenu,
  DeviceMetadataDialog,
  type DeviceMetadataDialogKind,
} from '@/components/devices/DeviceMetadataControls';
import { BindDeviceDialog, UnbindDeviceDialog } from '@/components/devices/DeviceProvisioningDialogs';
import { formatDateTime, formatRelative } from '@/lib/format';

export function DevicesPage(): JSX.Element {
  const [page,setPage]=useState(1);
  const { data, isLoading, isError, error, refetch, isFetching } = useDevices(true,page,20);
  const { isMock } = useAuth();
  const { data: capabilities } = useCapabilities();
  const refreshOne = useRefreshLocation();
  const refreshAll = useRefreshAll();
  const archive = useArchiveDevice();
  const restore = useRestoreDevice();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('name');
  const [dialog, setDialog] = useState<{ device: Device; kind: DeviceMetadataDialogKind } | null>(null);
  const [bindDialog, setBindDialog] = useState<{ initialDeviceNum: string } | null>(null);
  const [unbindTarget, setUnbindTarget] = useState<Device | null>(null);
  const [toast, setToast] = useState<{ text: string; error?: boolean } | null>(null);

  // Le mock est toujours autorisé ; en réel, ALLOW_REAL_BIND_UNBIND côté serveur décide.
  const realOperationsAllowed = isMock || capabilities?.realBindUnbindAllowed === true;

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 4_000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (isLoading) return <LoadingState label="Chargement des devices…" />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const allDevices = data?.devices ?? [];
  const archivedCount = allDevices.filter((d) => Boolean(d.localMetadata?.archivedAt)).length;
  const devices = allDevices.filter((d) => {
    if (d.localMetadata?.archivedAt) return false;
    const q=search.trim().toLowerCase();
    if(q && ![deviceDisplayName(d),d.deviceNum,d.mac??''].some(v=>v.toLowerCase().includes(q))) return false;
    return true;
  }).sort((a,b)=>sort==='battery'?(a.battery??101)-(b.battery??101):sort==='update'?Date.parse(b.lastUpdate??'0')-Date.parse(a.lastUpdate??'0'):deviceDisplayName(a).localeCompare(deviceDisplayName(b)));
  const missingKnown = KNOWN_DEVICE_NUMS.filter(
    (deviceNum) => !devices.some((device) => device.deviceNum === deviceNum),
  );

  const toggleArchive = (device: Device) => {
    const isArchived = Boolean(device.localMetadata?.archivedAt);
    const mutation = isArchived ? restore : archive;
    mutation.mutate(device.deviceNum, {
      onSuccess: () => setToast({ text: isArchived ? 'Appareil restauré.' : 'Appareil archivé.' }),
      onError: (mutationError) =>
        setToast({
          text: mutationError instanceof Error ? mutationError.message : 'Action impossible.',
          error: true,
        }),
    });
  };

  return (
    <>
      <PageHeader
        title="Devices"
        description={
          archivedCount > 0
            ? `${devices.length} SmartTag(s) affiché(s) · ${archivedCount} archivé(s).`
            : `${devices.length} SmartTag(s) lié(s) au compte.`
        }
        actions={
          <>
            <Button
              variant="outline"
              icon={<Plus className="h-4 w-4" />}
              disabled={!realOperationsAllowed}
              title={realOperationsAllowed ? undefined : "Association réelle désactivée côté serveur (ALLOW_REAL_BIND_UNBIND)."}
              onClick={() => setBindDialog({ initialDeviceNum: '' })}
            >
              Lier un device
            </Button>
            <Button
              variant="outline"
              icon={<RefreshCw className="h-4 w-4" />}
              loading={isFetching}
              onClick={() => void refetch()}
            >
              Recharger
            </Button>
            <Button
              icon={<RefreshCw className="h-4 w-4" />}
              loading={refreshAll.isPending}
              disabled={devices.length === 0}
              onClick={() => { const unique=[...new Set(devices.map(d=>d.deviceNum))]; if(window.confirm(`Demander l'actualisation de ${unique.length} appareil(s), en ${Math.ceil(unique.length/100)} lot(s) de 100 maximum ? Une acceptation ne garantit pas une nouvelle position immédiate.`)) refreshAll.mutate(unique); }}
            >
              Tout rafraîchir
            </Button>
          </>
        }
      />

      <Card><CardContent className="grid gap-3 pt-4 sm:grid-cols-2 sm:pt-5">
        <Field label="Rechercher"><Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Nom, deviceNum ou MAC" /></Field>
        <Field label="Tri"><Select value={sort} onChange={e=>setSort(e.target.value)}><option value="name">Nom</option><option value="battery">Batterie</option><option value="update">Dernière mise à jour</option></Select></Field>
      </CardContent></Card>

      {!realOperationsAllowed ? (
        <Alert tone="warning" title="Association et dissociation réelles désactivées">
          <p className="flex items-center gap-2">
            <ShieldOff className="h-4 w-4 shrink-0" aria-hidden />
            L’administrateur serveur doit activer <code className="font-mono">ALLOW_REAL_BIND_UNBIND</code> pour
            autoriser les opérations réelles vers Vernal. Les actions concernées sont désactivées ci-dessous.
          </p>
        </Alert>
      ) : null}

      {missingKnown.length > 0 ? (
        <Alert tone="info" title="Devices de test non liés">
          <div className="space-y-2">
            <p>
              Ces numéros fournis par le fournisseur ne sont pas présents dans la liste du compte :
            </p>
            <div className="flex flex-wrap gap-2">
              {missingKnown.map((deviceNum) => (
                <Button
                  key={deviceNum}
                  size="sm"
                  variant="outline"
                  className="font-mono"
                  disabled={!realOperationsAllowed}
                  icon={<Link2 className="h-3.5 w-3.5" />}
                  onClick={() => setBindDialog({ initialDeviceNum: deviceNum })}
                >
                  {deviceNum}
                </Button>
              ))}
            </div>
          </div>
        </Alert>
      ) : null}

      {refreshOne.isError ? <ErrorState error={refreshOne.error} title="Rafraîchissement impossible" /> : null}
      {refreshAll.isError ? <ErrorState error={refreshAll.error} title="Rafraîchissement impossible" /> : null}
      {refreshAll.isSuccess && refreshAll.data.summary ? <Alert tone="info" title="Demande groupée acceptée">{refreshAll.data.summary.requested} demandé(s), {refreshAll.data.summary.accepted} accepté(s), {refreshAll.data.summary.failed} échec(s), {refreshAll.data.summary.batchCount} lot(s). Une acceptation ne garantit pas une nouvelle position immédiate.</Alert> : null}
      {refreshAll.isSuccess ? <Card><CardHeader><CardTitle>Résultat par appareil</CardTitle></CardHeader><CardContent className="space-y-2">{refreshAll.data.results.map((result)=><div key={result.deviceNum} className="flex flex-col justify-between gap-1 rounded-md border border-border p-3 text-sm sm:flex-row sm:items-center"><span className="font-mono text-xs">{result.deviceNum}</span><span><Badge tone={result.requested?'success':'danger'}>{result.requested?'Demande acceptée':'Demande rejetée'}</Badge> <span className="ml-2">{result.message}</span></span><span className="text-xs text-muted-foreground">Position : {formatDateTime(result.device?.lastUpdate ?? result.location?.timestamp ?? null)} · Batterie : {result.device?.battery ?? result.location?.battery ?? 'Non disponible'}{result.device?.battery != null || result.location?.battery != null ? ' %' : ''}</span></div>)}</CardContent></Card> : null}
      {toast ? <Alert tone={toast.error ? 'danger' : 'success'} title={toast.text} /> : null}

      {devices.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Tags className="h-5 w-5" />}
            title="Aucun device lié"
            description="Lier un SmartTag avec son numéro pour démarrer le suivi."
            action={
              <Button
                icon={<Plus className="h-4 w-4" />}
                disabled={!realOperationsAllowed}
                onClick={() => setBindDialog({ initialDeviceNum: '' })}
              >
                Lier un device
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          {/* Vue tableau (tablette et desktop) */}
          <Card className="hidden md:block">
            <Table>
              <thead>
                <tr>
                  <Th>Device</Th>
                  <Th>Connexion fournisseur</Th>
                  <Th>Batterie</Th>
                  <Th>Position</Th>
                  <Th>Dernière position reçue</Th>
                  <Th>Fraîcheur</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr key={device.deviceNum} className="hover:bg-muted/40">
                    <Td>
                      <span className="block font-medium">{deviceDisplayName(device)}</span>
                      {device.localMetadata?.localName ? (
                        <span className="block text-[11px] text-muted-foreground">
                          Nom fournisseur : {device.name}
                        </span>
                      ) : null}
                      <span className="block font-mono text-[11px] text-muted-foreground">
                        {device.deviceNum}
                      </span>
                      <span className="block font-mono text-[11px] text-muted-foreground">MAC : {device.mac ?? 'Non disponible'}</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {device.known ? <Badge tone="info">Device de test</Badge> : null}
                        {device.localMetadata?.archivedAt ? <Badge tone="warning">Archivé</Badge> : null}
                        {deviceAssignmentSummary(device) ? (
                          <Badge tone="neutral">{deviceAssignmentSummary(device)}</Badge>
                        ) : null}
                      </div>
                    </Td>
                    <Td>
                      <StatusBadge status={device.status} />
                    </Td>
                    <Td>
                      <BatteryGauge level={device.battery} showBar />
                    </Td>
                    <Td className="tabular font-mono text-xs">
                      {formatCoords(device.latitude, device.longitude)}
                    </Td>
                    <Td>
                      <span className="block text-xs">{formatRelative(device.lastUpdate)}</span>
                      <span className="block text-[11px] text-muted-foreground">
                        {formatDateTime(device.lastUpdate)}
                      </span>
                    </Td>
                    <Td>
                      <PositionFreshnessBadge lastUpdate={device.lastUpdate} />
                    </Td>
                    <Td>
                      <div className="flex justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          loading={refreshOne.isPending && refreshOne.variables === device.deviceNum}
                          icon={<RefreshCw className="h-3.5 w-3.5" />}
                          onClick={() => refreshOne.mutate(device.deviceNum)}
                        >
                          Rafraîchir
                        </Button>
                        <Link to={`/devices/${device.deviceNum}`}>
                          <Button size="sm" variant="secondary" icon={<Eye className="h-3.5 w-3.5" />}>
                            Détails
                          </Button>
                        </Link>
                        <DeviceActionsMenu
                          device={device}
                          onRename={() => setDialog({ device, kind: 'rename' })}
                          onArchiveToggle={() => toggleArchive(device)}
                          onUnbind={() => setUnbindTarget(device)}
                          unbindDisabled={!realOperationsAllowed}
                          onMessage={(text, error) => setToast({ text, error })}
                        />
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
          <div className="flex items-center justify-between text-sm"><span>Page {data?.current??page} sur {data?.pages??1} · {data?.total??devices.length} appareil(s)</span><div className="flex gap-2"><Button size="sm" variant="outline" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Précédent</Button><Button size="sm" variant="outline" disabled={page>=(data?.pages??1)} onClick={()=>setPage(p=>p+1)}>Suivant</Button></div></div>

          {/* Vue cartes (mobile) */}
          <div className="grid gap-3 md:hidden">
            {devices.map((device) => (
              <DeviceCard
                key={device.deviceNum}
                device={device}
                refreshing={refreshOne.isPending && refreshOne.variables === device.deviceNum}
                onRefresh={() => refreshOne.mutate(device.deviceNum)}
                onUnbind={() => setUnbindTarget(device)}
                unbindDisabled={!realOperationsAllowed}
                onRename={() => setDialog({ device, kind: 'rename' })}
                onArchiveToggle={() => toggleArchive(device)}
                onMessage={(text, error) => setToast({ text, error })}
              />
            ))}
          </div>
        </>
      )}

      {dialog ? (
        <DeviceMetadataDialog
          device={dialog.device}
          kind={dialog.kind}
          onClose={() => setDialog(null)}
          onSaved={(text) => setToast({ text })}
        />
      ) : null}

      {bindDialog ? (
        <BindDeviceDialog
          existingDeviceNums={allDevices.map((device) => device.deviceNum)}
          initialDeviceNum={bindDialog.initialDeviceNum}
          onClose={() => setBindDialog(null)}
        />
      ) : null}

      {unbindTarget ? (
        <UnbindDeviceDialog device={unbindTarget} onClose={() => setUnbindTarget(null)} />
      ) : null}
    </>
  );
}

function DeviceCard({
  device,
  refreshing,
  onRefresh,
  onUnbind,
  unbindDisabled,
  onRename,
  onArchiveToggle,
  onMessage,
}: {
  device: Device;
  refreshing: boolean;
  onRefresh: () => void;
  onUnbind: () => void;
  unbindDisabled: boolean;
  onRename: () => void;
  onArchiveToggle: () => void;
  onMessage: (message: string, error?: boolean) => void;
}): JSX.Element {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="truncate">{deviceDisplayName(device)}</CardTitle>
            <p className="font-mono text-[11px] text-muted-foreground">{device.deviceNum}</p>
            <div className="mt-1 flex flex-wrap gap-1">
              {device.localMetadata?.archivedAt ? <Badge tone="warning">Archivé</Badge> : null}
              {deviceAssignmentSummary(device) ? (
                <Badge tone="neutral">{deviceAssignmentSummary(device)}</Badge>
              ) : null}
            </div>
          </div>
          <DeviceActionsMenu
            device={device}
            onRename={onRename}
            onArchiveToggle={onArchiveToggle}
            onUnbind={onUnbind}
            unbindDisabled={unbindDisabled}
            onMessage={onMessage}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="font-mono text-[11px] text-muted-foreground">MAC : {device.mac ?? 'Non disponible'}</p>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">Connexion fournisseur</span>
          <StatusBadge status={device.status} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Batterie</span>
          <BatteryGauge level={device.battery} showBar />
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">Position</span>
          <span className="tabular font-mono text-xs">
            {formatCoords(device.latitude, device.longitude)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">Dernière position reçue</span>
          <span className="text-right text-xs">
            <span className="block">{formatRelative(device.lastUpdate)}</span>
            <span className="text-muted-foreground">{formatDateTime(device.lastUpdate)}</span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">Fraîcheur</span>
          <PositionFreshnessBadge lastUpdate={device.lastUpdate} />
        </div>
        <div className="grid grid-cols-2 gap-2 pt-2">
          <Button
            size="sm"
            variant="outline"
            loading={refreshing}
            icon={<RefreshCw className="h-3.5 w-3.5" />}
            onClick={onRefresh}
          >
            Rafraîchir
          </Button>
          <Link to={`/devices/${device.deviceNum}`}>
            <Button size="sm" variant="secondary" className="w-full" icon={<Eye className="h-3.5 w-3.5" />}>
              Détails
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
